import axios from 'axios'
import { bffBaseURL, normalizeError } from './apiClient'
import { ApiResponseError } from './jobApi'
import type { components } from '@/types/generated'

// ============================================================================
// Employees API — Phase 57 HR Management
// ============================================================================

export async function getEmployee(employeeId: string) {
  try {
    const response = await axios.get(
      `${bffBaseURL}/api/v1/employees/${employeeId}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to get employee')
      throw new Error(message)
    }
    throw error
  }
}

// Phase 113 D8 — an employee's assignments (current + historical), each with
// project/client/area context. Powers the Colaboradores detail page.
export async function getEmployeeAssignments(employeeId: string) {
  try {
    const response = await axios.get(
      `${bffBaseURL}/api/v1/employees/${employeeId}/assignments`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to load employee assignments')
      throw new Error(message)
    }
    throw error
  }
}

export async function getEmployees(params: Record<string, any> = {}) {
  try {
    const queryString = new URLSearchParams(params).toString()
    const response = await axios.get(
      `${bffBaseURL}/api/v1/employees${queryString ? '?' + queryString : ''}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to load employees')
      throw new Error(message)
    }
    throw error
  }
}

export async function updateEmployee(employeeId: string, updates: any) {
  try {
    const response = await axios.patch(
      `${bffBaseURL}/api/v1/employees/${employeeId}`,
      updates
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to update employee')
      throw new Error(message)
    }
    throw error
  }
}

// ============================================================================
// Manual Colaborador — Phase 121 F1 (Stage 14.x)
//
// Typed end to end from the generated contract (X2/DL-16), matching the
// jobApi getJobNotes/addJobNote pattern: no hand-written DTOs, no `any` on the
// response, 4xx statuses surfaced as ApiResponseError so the wizard can render
// them inline.
// ============================================================================

export type ManualEmployeeCreatePayload = components['schemas']['ManualEmployeeCreateRequest']
export type ManualEmployeeResponse = components['schemas']['EmployeeResponse']
export type ManualCreateFieldError = components['schemas']['ValidationError']

/**
 * Error thrown by `createManualEmployee` when the upstream rejects the
 * request. Carries the parsed 422 `ValidationError[]` (typed from the
 * generated contract — never `any`) so the wizard can map each failure to the
 * field/step that owns it instead of rendering a single opaque message.
 */
export class ManualCreateError extends ApiResponseError {
  constructor(
    message: string,
    status: number,
    public readonly fieldErrors: ManualCreateFieldError[]
  ) {
    super(message, status)
    this.name = 'ManualCreateError'
  }
}

/**
 * Parse a FastAPI-style `detail` payload into typed field errors. Accepts the
 * 422 `ValidationError[]` shape; anything else yields `[]` so callers fall
 * back to the normalized message.
 */
function parseFieldErrors(detail: unknown): ManualCreateFieldError[] {
  if (!Array.isArray(detail)) return []
  const errors: ManualCreateFieldError[] = []
  for (const item of detail) {
    if (
      typeof item !== 'object' ||
      item === null ||
      !Array.isArray((item as { loc?: unknown }).loc) ||
      typeof (item as { msg?: unknown }).msg !== 'string'
    ) {
      continue
    }
    errors.push(item as ManualCreateFieldError)
  }
  return errors
}

/**
 * Create a colaborador manually (no CV) — atomic candidate + skills + employee
 * + current assignment in ONE backend transaction.
 *
 * Payload fields mirror ingestion field-for-field (task 13.2); validation
 * (nonblank name, end<start, skill existence) is owned upstream and passes
 * back as a faithful 400/422 via `ManualCreateError` — its `fieldErrors`
 * carry the typed 422 detail so the wizard can surface each error next to the
 * field/step that owns it. Responses are typed from the generated contract
 * (X2/DL-16): no hand-written DTOs, no `any` on the response.
 */
export async function createManualEmployee(
  data: ManualEmployeeCreatePayload
): Promise<ManualEmployeeResponse> {
  try {
    const response = await axios.post(
      `${bffBaseURL}/api/v1/employees/manual`,
      data
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status ?? 0
      const detail = (error.response?.data as { detail?: unknown } | undefined)
        ?.detail
      const fieldErrors = parseFieldErrors(detail)
      const message =
        fieldErrors.length > 0
          ? fieldErrors.map((fe) => fe.msg).join(' · ')
          : normalizeError(detail || 'No se pudo crear el colaborador')
      throw new ManualCreateError(message, status, fieldErrors)
    }
    throw error
  }
}
